import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { SearchFilterField, SearchFilterSpec, SearchRouteKey } from '../models/search-filter-spec.model';
import { SearchArrivalRegistry } from '../state/search-arrival.registry';

interface SearchRouteEntry {
  path: string;
  /** Canonical field → the target page's OWN store field name. licenseTerm is deliberately
      absent from every fieldMap — it bypasses store.patch() entirely (see navigate() below). */
  fieldMap: Partial<Record<Exclude<SearchFilterField, 'licenseTerm'>, string>>;
}

const IDENTITY_FIELD_MAP: SearchRouteEntry['fieldMap'] = {
  entities: 'entities', licenseType: 'licenseType', status: 'status',
  department: 'department', role: 'role', user: 'user',
  statutory: 'statutory', licenseNo: 'licenseNo',
  startDate: 'startDate', endDate: 'endDate',
};

/** 'documents' is added by a follow-up plan alongside its FilterRestoreCoordinator wiring. */
export const SEARCH_ROUTE_TABLE: Record<SearchRouteKey, SearchRouteEntry> = {
  workspace: { path: '/workspace', fieldMap: IDENTITY_FIELD_MAP },
  reports: { path: '/reports', fieldMap: IDENTITY_FIELD_MAP },
};

@Injectable({ providedIn: 'root' })
export class SearchRoutingService {
  private readonly router = inject(Router);
  private readonly arrival = inject(SearchArrivalRegistry);

  /**
   * Navigates to a search interpretation's target page — UNLESS the user is already on it.
   * `app.config.ts` uses plain `provideRouter(routes)` with no `withRouterConfig`, so Angular's
   * `onSameUrlNavigation` defaults to `'ignore'`: `router.navigate()` to the CURRENT url is
   * silently dropped (`NavigationSkipped`, no error, no `NavigationEnd`). That is the whole bug
   * this branch fixes — search from Dashboard always lands on a DIFFERENT url and works, search
   * from Workspace/Reports targeting the same page is a same-url no-op and does nothing.
   *
   * Forcing the router to re-process the navigation would not be enough on its own, either: the
   * default `RouteReuseStrategy` reuses the SAME component instance for the SAME route config, so
   * `ngOnInit` — where the target page applies search filters via `FilterRestoreCoordinator
   * .restoreOnEnter`'s `onDefault` hook — would never re-run. So the already-on-target case
   * broadcasts on {@link SearchArrivalRegistry.live$} instead, which the currently-mounted page
   * subscribes to directly, independent of router/component lifecycle. `stage()` still runs in
   * both branches so the arrival chips behave identically either way.
   */
  navigate(spec: SearchFilterSpec): void {
    const entry = SEARCH_ROUTE_TABLE[spec.route];
    const state = this.buildState(spec, entry);
    this.arrival.stage(spec.matchedTerms);
    if (this.isAlreadyOnTarget(entry.path)) {
      this.arrival.notifyLive({ route: spec.route, state });
      return;
    }
    void this.router.navigate([entry.path], { state });
  }

  /** Translates a search interpretation's canonical filters into the target page's own nav-state
      shape via `entry.fieldMap`. `licenseTerm` bypasses the map entirely (see `fieldMap`'s doc). */
  private buildState(spec: SearchFilterSpec, entry: SearchRouteEntry): Record<string, unknown> {
    const state: Record<string, unknown> = { fromSearch: true };
    for (const [field, value] of Object.entries(spec.filters)) {
      if (field === 'licenseTerm') {
        state['licenseTerm'] = value;
        continue;
      }
      const targetKey = entry.fieldMap[field as Exclude<SearchFilterField, 'licenseTerm'>];
      if (targetKey) state[targetKey] = value;
    }
    return state;
  }

  /** True when the router's current url already matches the search target's path (query string
      and fragment stripped — a search arrival while e.g. `/workspace?x=1` is showing is still
      "already on target"). */
  private isAlreadyOnTarget(path: string): boolean {
    return this.router.url.split('?')[0].split('#')[0] === path;
  }
}
